import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  FilterList,
  Clear,
  Casino,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchWinningPatterns,
  deleteWinningPattern,
  setFilters,
  clearFilters,
} from '../store/slices/winningPatternsSlice';
import { WinningPatternData, WinningPatternFilters } from '../types';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import WinningPatternFormModal from '../components/winningPatterns/WinningPatternFormModal';
import WinningPatternDetailsModal from '../components/winningPatterns/WinningPatternDetailsModal';

const WinningPatternsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { patterns, pagination, loading, error } = useSelector(
    (state: RootState) => state.winningPatterns
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [patternTypeFilter, setPatternTypeFilter] = useState<string>('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingPattern, setEditingPattern] = useState<WinningPatternData | null>(null);
  const [selectedPattern, setSelectedPattern] = useState<WinningPatternData | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchWinningPatterns({ page: 1, limit: 20 }) as any);
  }, [dispatch]);

  const handleSearch = () => {
    const filters: WinningPatternFilters = {};
    if (searchTerm) filters.search = searchTerm;
    if (patternTypeFilter) filters.patternType = patternTypeFilter as 'builtin' | 'custom';
    
    dispatch(setFilters(filters));
    dispatch(fetchWinningPatterns({ page: 1, limit: 20, filters }) as any);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setPatternTypeFilter('');
    dispatch(clearFilters());
    dispatch(fetchWinningPatterns({ page: 1, limit: 20 }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(fetchWinningPatterns({ page: newPage + 1, limit: 20 }) as any);
  };

  const handleViewPattern = (pattern: WinningPatternData) => {
    setSelectedPattern(pattern);
    setShowDetailsModal(true);
  };

  const handleEditPattern = (pattern: WinningPatternData) => {
    setEditingPattern(pattern);
    setShowFormModal(true);
  };

  const handleCreatePattern = () => {
    setEditingPattern(null);
    setShowFormModal(true);
  };

  const handleDeletePattern = async (pattern: WinningPatternData) => {
    setSelectedPattern(pattern);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPattern) return;
    
    setDeleteLoading(true);
    try {
      const result = await dispatch(deleteWinningPattern(selectedPattern.id!) as any);
      if (result.payload?.success) {
        toast.success('Winning pattern deleted successfully');
        setShowDeleteDialog(false);
        dispatch(fetchWinningPatterns({ page: pagination.page, limit: 20 }) as any);
      }
    } catch (error) {
      toast.error('Failed to delete winning pattern');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSuccess = () => {
    dispatch(fetchWinningPatterns({ page: pagination.page, limit: 20 }) as any);
  };

  const getPatternTypeColor = (patternType: string) => {
    switch (patternType) {
      case 'builtin': return 'primary';
      case 'custom': return 'secondary';
      default: return 'default';
    }
  };

  const getBuiltinTypeLabel = (builtinType?: string) => {
    switch (builtinType) {
      case 'standard': return 'Standard';
      case 'fourCorners': return 'Four Corners';
      case 'fullHouse': return 'Full House';
      case 'diamond': return 'Diamond';
      case 'xPattern': return 'X Pattern';
      default: return 'Unknown';
    }
  };

  const renderCustomPattern = (pattern: number[][]) => {
    if (!pattern || pattern.length === 0) return 'No pattern';
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {pattern.map((row, rowIndex) => (
          <Box key={rowIndex} sx={{ display: 'flex', gap: 0.5 }}>
            {row.map((cell, cellIndex) => (
              <Box
                key={cellIndex}
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: cell === 1 ? 'primary.main' : 'grey.300',
                  borderRadius: 1,
                }}
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 1, sm: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: { xs: 0.5, sm: 2 } }}>
            Winning Patterns
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage custom and built-in winning patterns for bingo games
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
            <TextField
              size="small"
              placeholder="Search patterns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Pattern Type</InputLabel>
              <Select
                value={patternTypeFilter}
                onChange={(e) => setPatternTypeFilter(e.target.value)}
                label="Pattern Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="builtin">Built-in</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={handleSearch}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Search
              </Button>
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Clear
              </Button>
            </Box>

            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreatePattern}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Create Pattern
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Patterns Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table sx={{ minWidth: { xs: 520, md: 800 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Built-in Type</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Pattern</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created By</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patterns.map((pattern) => (
                <TableRow 
                  key={pattern.id}
                  onClick={() => handleViewPattern(pattern)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                >
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body1" fontWeight="bold">
                      {pattern.name}
                    </Typography>
                    {pattern.visualPattern && (
                      <Typography variant="caption" color="textSecondary">
                        {pattern.visualPattern}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={pattern.patternType}
                        color={getPatternTypeColor(pattern.patternType) as any}
                        size="small"
                        sx={{ width: 'fit-content', minWidth: '80px' }}
                      />
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="textSecondary">
                          {pattern.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {pattern.createdBy?.username || 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {pattern.patternType === 'builtin' && pattern.builtinType ? (
                      <Chip
                        label={getBuiltinTypeLabel(pattern.builtinType)}
                        variant="outlined"
                        size="small"
                        sx={{ width: 'fit-content', minWidth: '80px' }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {pattern.patternType === 'custom' && pattern.customPattern ? (
                      renderCustomPattern(pattern.customPattern)
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Built-in
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {pattern.description}
                      </Typography>
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="textSecondary">
                          {pattern.createdAt ? dayjs(pattern.createdAt).format('MMM DD, YYYY') : '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={pattern.isActive ? 'Active' : 'Inactive'}
                        color={pattern.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ width: 'fit-content', minWidth: '80px' }}
                      />
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                        {pattern.patternType === 'builtin' && pattern.builtinType && (
                          <Chip
                            label={getBuiltinTypeLabel(pattern.builtinType)}
                            variant="outlined"
                            size="small"
                            sx={{ width: 'fit-content', minWidth: '80px' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">
                      {pattern.createdBy?.username || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {pattern.createdAt ? dayjs(pattern.createdAt).format('MMM DD, YYYY') : '-'}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewPattern(pattern);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Pattern">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditPattern(pattern);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Pattern">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePattern(pattern);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={() => {}}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Form Modal */}
      <WinningPatternFormModal
        pattern={editingPattern}
        open={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSuccess={handleFormSuccess}
      />

      {/* Details Modal */}
      <WinningPatternDetailsModal
        pattern={selectedPattern}
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Winning Pattern</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete "{selectedPattern?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : undefined}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WinningPatternsPage; 